import React, { useEffect, PropsWithChildren } from 'react';
import { Command } from 'cmdk';
import { ArrowLeftIcon } from 'lucide-react';
import { useCommandPaletteContext } from './CommandPaletteContext';
import { CommandList } from './components/CommandList';
import { Footer } from './components/CommandFooter';
import { ScoringDebugPanel } from './components/ScoringDebugPanel';
import { CommandsPlugin, NavigationPlugin, SystemPlugin, GraphPlugin, HelloWorldPlugin, DocumentsPlugin, TenancyPlugin, CalculatorPlugin, InterviewPlugin, InterviewControlsPlugin, FindByIdPlugin } from './plugins';
import { ParsedGraphContextProvider } from '@/pages/models/release/GraphContext';
import { ScoringDebugProvider } from './ScoringDebugContext';
import styles from './CommandPalette.module.scss';

/**
 * Internal UI component for the command palette dialog.
 *
 * Always renders the `Command` wrapper and the `Command.Input`. When in
 * global mode it renders a `CommandList` with all registered global commands.
 * When navigated to a plugin page it renders that page's registered component
 * instead, giving it full control over the list area.
 */
const CommandPaletteUI: React.FC<{
  emptyMessage?: string;
}> = ({
  emptyMessage = 'No commands found.',
}) => {
    const {
      isOpen,
      searchValue,
      setSearchValue,
      placeholder,
      currentPage,
      navigateBack,
      close,
      getAllCommands,
      getActivePageComponent,
    } = useCommandPaletteContext();

    // Resolve the active page component (if any) once per render.
    // When null we fall back to the global CommandList.
    const ActivePage = getActivePageComponent();
    // globalCommands is derived from reactive command registry state, so it is
    // always up-to-date when the palette renders.
    const globalCommands = getAllCommands();

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (currentPage) {
          navigateBack();
        } else {
          close();
        }
      }
    };

    // Focus input whenever the palette opens or when currentPage changes (e.g. navigating back to global list)
    useEffect(() => {
      if (isOpen) {
        setTimeout(() => {
          const input = document.querySelector('[cmdk-input]') as HTMLInputElement;
          if (input) input.focus();
        }, 0);
      }
    }, [isOpen, currentPage]);

    if (!isOpen) {
      return null;
    }

    return (
      <div className={styles.overlay} onClick={close}>
        <Command
          className={styles.dialog}
          shouldFilter={false}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.inputContainer}>
            {currentPage && (
              <button
                className={styles.backButton}
                onClick={navigateBack}
                title="Go back"
              >
                <ArrowLeftIcon />
              </button>
            )}
            <Command.Input
              autoFocus
              placeholder={placeholder}
              value={searchValue}
              onValueChange={setSearchValue}
            />
          </div>

          {ActivePage
            ? <ActivePage />
            : (
              <>
                <CommandList commands={globalCommands} emptyMessage={emptyMessage} />
                <Footer />
              </>
            )
          }
        </Command>
      </div>

    );
  };

/**
 * CommandPalette - Main component that provides context and renders the UI.
 *
 * @example
 * ```tsx
 * <CommandPalette>
 *   <SystemPlugin />
 *   <NavigationPlugin />
 * </CommandPalette>
 * ```
 */
export type CommandPaletteProps = PropsWithChildren<{
  emptyMessage?: string;
}>;

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  emptyMessage,
}) => {
  return (
    <ScoringDebugProvider>
      <ParsedGraphContextProvider>
        {/* Render plugins - they register commands/pages but don't render UI */}
        <SystemPlugin />
        <NavigationPlugin />
        <CommandsPlugin />
        <GraphPlugin />
        <HelloWorldPlugin />
        <DocumentsPlugin />
        <TenancyPlugin />
        <CalculatorPlugin />
        <InterviewPlugin />
        <InterviewControlsPlugin />
        <FindByIdPlugin />

        {/* UI reads from registry and renders global list or active page */}
        <CommandPaletteUI emptyMessage={emptyMessage} />
      </ParsedGraphContextProvider>
    </ScoringDebugProvider>
  );
};
